import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import Highcharts from "highcharts/highcharts";
import HighchartsReact from "highcharts-react-official";

import mapModule from "highcharts/modules/map";
import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";
import brMap from "@highcharts/map-collection/countries/br/br-all.geo.json";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(mapModule, Highcharts);
initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

function readCssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return (value || "").trim() || fallback;
}

export default function BrazilStateMapChart({
  data = [],
  loading = false,
  title = "Mapa por estado",
  exportMode = false,
}) {
  const chartRef = useRef(null);
  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px var(--border-color)",
        overflow: "hidden",
        minHeight: 360,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        map: brMap,
        animation: !exportMode,
        spacing: exportMode ? [0, 0, 0, 0] : [24, 16, 16, 24],
        backgroundColor: "transparent",
        height: exportMode ? 560 : undefined,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: "#614a9e" },
      },

      exporting: { enabled: false },
      

      mapNavigation: { enabled: false },

      colorAxis: {
        min: 0,
        stops: [
          [0, "#F3E8FF"],
          [0.4, "#C4B5FD"],
          [0.7, "#7C3AED"],
          [1, "#4C1D95"],
        ],
        labels: {
          style: {
            color: "var(--text-color)",
            fontSize: exportMode ? "12px" : "11px",
            fontWeight: "600",
          },
        },
      },

      legend: exportMode
        ? { enabled: false }
        : {
            layout: "vertical",
            align: "right",
            verticalAlign: "middle",
            symbolHeight: 200,
            title: {
              text: "Quantidade",
              style: { fontWeight: "800", color: "var(--text-color)" },
            },
            itemStyle: { fontSize: "12px" },
          },

      tooltip: {
        useHTML: true,
        pointFormat:
          `<div style="font-weight:800;margin-bottom:4px">{point.name}</div>` +
          `<div>Total: <b>{point.value}</b></div>`,
      },

      plotOptions: {
        series: { animation: !exportMode },
        map: {
          borderColor: "rgba(76,29,149,0.35)",
          borderWidth: 1,
          nullColor: "rgba(148,163,184,0.25)",
          states: { hover: { brightness: 0.08, borderColor: "#4C1D95" } },
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            crop: false,
            overflow: "allow",
            formatter: function () {
              const v = this.point.value ?? 0;
              if (!v) return "";
              const key = (this.point["hc-key"] || "").split("-")[1] || "";
              return `${key.toUpperCase()}: ${v}`;
            },
            style: {
              fontSize: exportMode ? "12px" : "11px",
              fontWeight: "900",
              color: "#0B1220",
              textOutline: "2px rgba(255,255,255,0.85)",
            },
          },
        },
      },

      series: [{ type: "map", name: "Clippings", data, joinBy: ["hc-key", 0] }],

      credits: { enabled: false },
    };
  }, [data, title, exportMode]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, data]);

  const handleExportPNG = async () => {
    const chart = chartRef.current?.chart;
    const target = chart?.renderTo;
    if (!target) return;

    const backgroundColor = readCssVar("--background-graphic", "#FFFFFF");

    try {
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      const canvas = await html2canvas(target, {
        scale: 2,
        backgroundColor,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        removeContainer: true,
      });

      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png", 1);
      link.download = "mapa-por-estado.png";
      link.click();
    } catch (error) {
      console.error("Erro ao exportar PNG do mapa por estado:", error);
    }
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,
    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    backgroundColor: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando mapa por estado...">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          constructorType="mapChart"
          containerProps={{ style: { height: exportMode ? 560 : 360 } }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
